<script setup lang="ts">
import {computed} from 'vue';
import type {LibraryComponent} from './library_component.ts';

const props = withDefaults(defineProps<{
  component: LibraryComponent;
  path: string[];
  cellClassNames?: string;
}>(), {
  cellClassNames: '',
});

const isChanged = computed(() => {
  return props.component.isPropertyChanged(props.path);
});

const highlights = computed(() => {
  return props.component.extractLineHighlights(props.path);
});
</script>

<template>
  <div v-if="isChanged">
    <div
      v-if="highlights.base.length > 0"
      :class="['cell removed', cellClassNames]"
    >
      <a
        v-if="props.component.shouldCreateHyperlink(props.component.base, path)"
        :href="props.component.getPath(props.component.base, path).toString()"
        target="_blank"
      >
        <span
          v-for="(part, partIndex) in highlights.base"
          :key="partIndex"
          :class="props.component.shouldHighlight(part) ? 'highlight' : ''"
        >
          {{ part.text }}
        </span>
      </a>
      <span
        v-for="(part, partIndex) in highlights.base"
        v-else
        :key="partIndex"
        :class="props.component.shouldHighlight(part) ? 'highlight' : ''"
      >
        {{ part.text }}
      </span>
    </div>
    <div
      v-if="highlights.head.length > 0"
      :class="['cell added', cellClassNames]"
    >
      <a
        v-if="props.component.shouldCreateHyperlink(props.component.head, path)"
        :href="props.component.getPath(props.component.head, path).toString()"
        target="_blank"
      >
        <span
          v-for="(part, partIndex) in highlights.head"
          :key="partIndex"
          :class="props.component.shouldHighlight(part) ? 'highlight' : ''"
        >
          {{ part.text }}
        </span>
      </a>
      <span
        v-for="(part, partIndex) in highlights.head"
        v-else
        :key="partIndex"
        :class="props.component.shouldHighlight(part) ? 'highlight' : ''"
      >
        {{ part.text }}
      </span>
    </div>
  </div>
  <div v-else>
    <a
      v-if="props.component.shouldCreateHyperlink(props.component.head, props.path)"
      :href="props.component.getPath(props.component.head, props.path).toString()"
      target="_blank"
    >
      {{ props.component.getPath(props.component.head, props.path) }}
    </a>
    <template v-else>
      {{ props.component.getPath(props.component.head, props.path) ?? '' }}
    </template>
  </div>
</template>

<style scoped>
.cell {
  max-width: 400px;
  word-wrap: break-word;
}
.cell.added {
  background: var(--color-diff-added-row-bg);
}

.cell.removed {
  background: var(--color-diff-removed-row-bg);
}

.cell.added .highlight {
  background: var(--color-diff-added-word-bg);
}

.cell.removed .highlight {
  background: var(--color-diff-removed-word-bg);
}
</style>
